'use client'

import { useEffect, useMemo, useState } from 'react'

import {
  type GraftcodeOnboardingScenarioId,
  type GraftcodeOnboardingSetupMode,
  resolveScenarioContentPath,
} from '../graftcode-onboarding.const'
import { applyGuideOsToTutorialContent } from '../lib/applyGuideOsToTutorialContent'
import type { GraftcodeOnboardingGuideOs } from '../lib/graftcode-onboarding-guide-os.const'
import {
  cacheOnboardingTutorialMarkdown,
  getCachedOnboardingTutorialMarkdown,
} from '../lib/onboarding-tutorial-content-cache'
import type { ParsedOnboardingTutorial } from '../lib/parseOnboardingTutorialMarkdown'
import { parseOnboardingTutorialMarkdown } from '../lib/parseOnboardingTutorialMarkdown'

type TutorialContentState = {
  markdown: string | null
  contentPath: string | null
  pendingPath: string | null
  error: string | null
}

function createInitialState(path: string | null): TutorialContentState {
  if (!path) {
    return { markdown: null, contentPath: null, pendingPath: null, error: null }
  }

  const cached = getCachedOnboardingTutorialMarkdown(path)
  if (cached) {
    return {
      markdown: cached,
      contentPath: path,
      pendingPath: null,
      error: null,
    }
  }

  return {
    markdown: null,
    contentPath: null,
    pendingPath: path,
    error: null,
  }
}

export function useOnboardingTutorialContent(
  scenarioId: GraftcodeOnboardingScenarioId | null,
  stackId: string | null,
  setupMode: GraftcodeOnboardingSetupMode,
  guideOs: GraftcodeOnboardingGuideOs
) {
  const path = scenarioId && stackId ? resolveScenarioContentPath(scenarioId, stackId, setupMode) : null

  const [state, setState] = useState<TutorialContentState>(() => createInitialState(path))
  const [readyFromCache, setReadyFromCache] = useState(() => Boolean(path && getCachedOnboardingTutorialMarkdown(path)))

  useEffect(() => {
    if (!path) {
      setReadyFromCache(false)
      setState({ markdown: null, contentPath: null, pendingPath: null, error: null })
      return
    }

    const cached = getCachedOnboardingTutorialMarkdown(path)
    if (cached) {
      setReadyFromCache(true)
      setState({
        markdown: cached,
        contentPath: path,
        pendingPath: null,
        error: null,
      })
      return
    }

    setReadyFromCache(false)
    let cancelled = false

    setState((prev) => ({
      ...prev,
      pendingPath: path,
      error: null,
    }))

    fetch(path)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load tutorial (${response.status})`)
        }
        return response.text()
      })
      .then((markdown) => {
        if (cancelled) {
          return
        }
        cacheOnboardingTutorialMarkdown(path, markdown)
        setReadyFromCache(true)
        setState({
          markdown,
          contentPath: path,
          pendingPath: null,
          error: null,
        })
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }
        setState((prev) => ({
          ...prev,
          pendingPath: null,
          error: error instanceof Error ? error.message : 'Failed to load tutorial content',
        }))
      })

    return () => {
      cancelled = true
    }
  }, [path])

  const loading = Boolean(path && state.pendingPath === path)

  const data = useMemo((): ParsedOnboardingTutorial | null => {
    if (!state.markdown) {
      return null
    }
    const parsed = parseOnboardingTutorialMarkdown(state.markdown)
    const withOs = applyGuideOsToTutorialContent(parsed, guideOs)
    return { ...withOs, tutorialVideo: withOs.tutorialVideo ?? null }
  }, [state.markdown, guideOs])

  return {
    data,
    loading,
    refreshing: loading,
    error: state.error,
    contentPath: state.contentPath,
    readyFromCache,
    activeContentKey: path ?? '',
    resolvedContentKey: state.contentPath ?? path ?? '',
  }
}
